import React, { FC, useEffect } from "react";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useLocation } from "@Libs/Router/hooks/useLocation";
import { useParams } from "@Libs/Router/hooks/useParams";
import { ServiceOfferConfirmationTypes } from "@modules/ServiceAssignment/Containers/ConfirmationMessagesRouter/ConfirmationMessagesRouter";
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";
import { acceptServiceOffer } from "@modules/ServiceAssignment/services/serviceOffers";
import { ServiceAssignmentEventStep } from "@modules/ServiceAssignment/types/serviceAssignment.types";
import { useRecordEvent } from "@Libs/Events/hooks/useRecordEvent";
import { trackEvent } from "@Libs/GrowthBook/services/growthbook";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { ServiceFeeFormulaSummary } from "@modules/ServiceFees/Components/ServiceFeeFormulaSummary/ServiceFeeFormulaSummary";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useAvailablePaymentMethods } from "@modules/PaymentProcessing/hooks/useAvailablePaymentMethods";
import { IAppRoute } from "@App/types";
import {
  SavePaymentPreferences,
  ISavePaymentPreferencesProps,
} from "@modules/PaymentProcessing/Flows/SavePaymentPreferences/SavePaymentPreferences";
import { useUuid } from "@Shared/hooks/useUuid";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";
import { GenericAlert } from "@UI/Components/GenericAlert/GenericAlert";

const BASE_SERVICE_FEE_FLOW_ROUTE: IAppRoute[] = [
  "SERVICE_OFFERS",
  "SERVICE_OFFER",
  "ACCEPTANCE",
  "SERVICE_FEE",
];

const SERVICE_OFFER_ALREADY_ACCEPTED_ERROR = "ServiceOffer already assigned";

export const ServiceOfferAcceptanceServiceFeeFlow: FC = () => {
  const navigate = useNavigate();
  const basketId = useUuid();
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const { serviceOfferId } = useParams(BASE_SERVICE_FEE_FLOW_ROUTE);
  const {
    serviceOffer,
    isLoading: serviceOfferIsLoading,
    refetch: refetchServiceOffer,
  } = useServiceOffer(serviceOfferId);
  const {
    enabledPaymentMethods,
    allowedPaymentMethods,
    isLoading: isAvailablePaymentMethodsLoading,
  } = useAvailablePaymentMethods({
    resourceType: "basket",
    resourceAttributes: {
      id: basketId,
      items: [
        { itemType: "service", itemId: Number(serviceOfferId), quantity: 1 },
      ],
    },
  });

  const { recordEvent: recordStepVisitedEvent } = useRecordEvent(
    {
      key: "contract_acceptance_flow.step_visited",
    },
    { triggerOnMount: false }
  );

  const handlePageMount = (
    route: "CHOOSE_PAYMENT_METHOD" | "CONFIGURE_PAYMENT_METHOD"
  ) => {
    recordStepVisitedEvent({
      data: {
        step: ServiceAssignmentEventStep[route],
        contractId: Number(serviceOfferId),
      },
    });
  };

  const onSuccess = (paymentMethod) => {
    navigate("LEGACY_SERVICE_OFFER_FLOWS_CONFIRMATION_PAGE", {
      reload: true,
      queryParams: {
        conversation_id: String(serviceOffer.conversationId),
        paymentMethod,
        confirmation: ServiceOfferConfirmationTypes.PAYMENT_METHOD_CONFIGURED,
      },
    });
  };

  const handleSubmitPaymentMethodConfig: ISavePaymentPreferencesProps["onSuccess"] =
    async ({ paymentMethod }) => {
      try {
        await acceptServiceOffer(serviceOfferId);
        // activation_rate experiment events — safe to remove once experiment is concluded
        await Promise.all([
          trackEvent("contract_assigned", {
            contractId: Number(serviceOfferId),
            serviceRequestId: serviceOffer?.serviceRequestId,
            clientId: currentUser?.id,
            clientEmail: currentUser?.email,
            formula: "service_fee",
          }),
          trackEvent("payment_preference_saved", {
            contractId: Number(serviceOfferId),
            serviceRequestId: serviceOffer?.serviceRequestId,
            clientId: currentUser?.id,
            clientEmail: currentUser?.email,
            isSubscribed: currentUser?.subscribed ?? false,
            paymentMethod,
            formula: "service_fee",
          }),
        ]);
        onSuccess(paymentMethod);
      } catch (e) {
        // handle if service offer was already accepted
        if (e?.response?.data?.error === SERVICE_OFFER_ALREADY_ACCEPTED_ERROR) {
          onSuccess(paymentMethod);
          return;
        }

        throw e;
      }
    };

  useEffect(() => {
    // Refetch service offer when route changes
    // to ensure we have the latest service offer state
    // and avoid trying to accept an already accepted service offer
    refetchServiceOffer();
  }, [location.pathname]);

  if (serviceOfferIsLoading || isAvailablePaymentMethodsLoading) {
    return <LoadingScreen />;
  }

  return (
    <SavePaymentPreferences
      state={{
        operationSummary: (
          <ServiceFeeFormulaSummary serviceOfferId={serviceOfferId} />
        ),
        allowedPaymentMethods,
        enabledPaymentMethods,
        paymentFormInfo: (
          <GenericAlert status="warning">
            <FormattedMessage {...messages.paymentOnWebsite} />
          </GenericAlert>
        ),
        nonProWarning:
          serviceOffer && !serviceOffer?.serviceProvider?.user?.listworkerProId,
      }}
      backRoute={{
        to: BASE_SERVICE_FEE_FLOW_ROUTE,
        params: { serviceOfferId },
      }}
      onSuccess={handleSubmitPaymentMethodConfig}
      onLeave={() => undefined}
      onPageMount={handlePageMount}
    />
  );
};
